/**
 * Pure, allocation-light strategy pricing kernel.
 *
 * This is the fast path that powers the payoff graph, the table, and the
 * draggable time slider. It produces the SAME numbers as `StrategyBuilder`'s
 * per-point loop (see strategyKernel.test.ts for the equivalence proof) but
 * without the three costs that made that loop slow:
 *
 *   1. No `new Option(...)` per point — the Option constructor parses the
 *      expiration through `Intl.DateTimeFormat` every time.
 *   2. No implied-volatility re-solve per point — IV is a per-leg constant,
 *      resolved once up front (by whoever built the leg params) and reused.
 *   3. No per-greek `calculateD1` recomputation — d1/d2 are computed once per
 *      (leg, spot, time) and every value/greek is derived from them.
 *
 * What changes when you drag time is a single number: `tMs` (the evaluation
 * timestamp), which sets each leg's time-to-expiry `T = (expiryMs - tMs)/yr`.
 * Everything else per leg is precomputed in `buildKernel`.
 */
import {
  OptionInputParams,
  UnderlyingStockPosition,
  OPTIONS_PER_CONTRACT,
  YEAR_IN_MS,
  normcdf,
  normpdf,
  isCall as isCallType,
  dateStringToMarketClose,
  INTRINSIC_LIMIT_TIME_YEARS,
  intrinsicOptionValue,
  expiryDelta,
} from './utils';
import { calculateReturnOnCost, ProfitPercentResult } from 'utils/shared/quantlib/returnOnCost';

export interface KernelLeg {
  strike: number;
  isCall: boolean;
  volatility: number; // implied vol, resolved once upstream
  riskFreeRate: number;
  signedContracts: number; // numberOfContracts (negative = short), used verbatim
  entryPremium: number; // cost basis per share
  expiryMs: number; // market-close UTC ms, precomputed once
}

export interface StrategyKernel {
  legs: KernelLeg[];
  stocks: UnderlyingStockPosition[];
  /** Σ entryPremium × signedContracts — constant in spot and time. */
  originalContractValue: number;
  /** Σ quantity × entryPrice — constant in spot and time. */
  originalStockPositionValue: number;
}

export interface StrategyPoint extends ProfitPercentResult {
  stockPrice: number;
  profitPerContract: number;
  profit: number;
  contractValue: number;
  delta: number;
  gamma: number;
  theta: number;
  vega: number;
  rho: number;
  isZeroCrossing?: boolean;
}

interface LegGreeks {
  premium: number;
  delta: number;
  gamma: number;
  theta: number;
  vega: number;
  rho: number;
}

const THETA_PER_DAY = 365;

/**
 * Black-Scholes value + greeks for a single leg, computing d1/d2 once. The
 * formulas mirror calculateD1/D2/Premium/Delta/Gamma/Theta/Vega/Rho exactly
 * (including the per-greek display scaling: vega & rho /100, theta /365).
 */
function evalLeg(
  spot: number,
  strike: number,
  timeToExpiry: number,
  volatility: number,
  rate: number,
  isCall: boolean,
): LegGreeks {
  // At/near expiry the greeks are singular (gamma/theta ~ 1/√T diverge, vega
  // collapses). Return the mathematical limit at expiry instead of evaluating
  // Black-Scholes at a degenerate √T. This MUST mirror calculatePremium /
  // calculateDelta / calculateGamma / … in BlackScholes.ts exactly, so the
  // kernel and the Option-based reference stay numerically identical
  // (strategyKernel.test.ts proves this point-for-point).
  if (timeToExpiry <= INTRINSIC_LIMIT_TIME_YEARS) {
    return {
      premium: intrinsicOptionValue(spot, strike, isCall),
      delta: expiryDelta(spot, strike, isCall),
      gamma: 0,
      theta: 0,
      vega: 0,
      rho: 0,
    };
  }

  // Exact S→0 no-dividend limit, after the unchanged expiry settlement guard.
  // Avoid computing gamma as 0/0 and relying on the downstream safe() mask.
  if (spot === 0 && strike > 0) {
    const discountedStrike = strike * Math.exp(-rate * timeToExpiry);
    return {
      premium: isCall ? 0 : discountedStrike,
      delta: isCall ? 0 : -1,
      gamma: 0,
      theta: isCall ? 0 : (rate * discountedStrike) / THETA_PER_DAY,
      vega: 0,
      rho: isCall ? 0 : (-discountedStrike * timeToExpiry) / 100,
    };
  }

  const T = timeToExpiry;
  const sqrtT = Math.sqrt(T);
  const volSqrtT = volatility * sqrtT;
  const logMoneyness = Math.log(spot / strike);

  const d1 = (logMoneyness + (rate + (volatility * volatility) / 2) * T) / volSqrtT;
  const d2 = (logMoneyness + (rate - (volatility * volatility) / 2) * T) / volSqrtT;

  const Nd1 = normcdf(d1);
  const Nd2 = normcdf(d2);
  const NnegD1 = normcdf(-d1);
  const NnegD2 = normcdf(-d2);
  const pdfD1 = normpdf(d1);
  const discount = Math.exp(-rate * T);

  const premium = isCall
    ? spot * Nd1 - strike * discount * Nd2
    : strike * discount * NnegD2 - spot * NnegD1;

  const delta = isCall ? Nd1 : -NnegD1;
  const gamma = pdfD1 / (spot * volatility * sqrtT);
  const vega = (spot * sqrtT * pdfD1) / 100;
  const theta = isCall
    ? (-(spot * pdfD1 * volatility) / (2 * sqrtT) - rate * strike * discount * Nd2) / THETA_PER_DAY
    : (-(spot * pdfD1 * volatility) / (2 * sqrtT) + rate * strike * discount * NnegD2) /
      THETA_PER_DAY;
  const rho = isCall
    ? (strike * T * discount * Nd2) / 100
    : (-strike * T * discount * NnegD2) / 100;

  return { premium, delta, gamma, theta, vega, rho };
}

/** Precompute the per-leg invariants once. Time-dragging never re-runs this. */
export function buildKernel(
  strategyOptionParams: OptionInputParams[],
  underlyingStockPositions: UnderlyingStockPosition[] = [],
): StrategyKernel {
  const legs: KernelLeg[] = strategyOptionParams.map((params) => {
    const signedContracts = params.numberOfContracts ?? 1;
    const entryPremium = params.premium ?? 0;
    const expiryMs = params.expirationDateString
      ? dateStringToMarketClose(params.expirationDateString).getTime()
      : // Fall back to deriving an absolute expiry from a relative time-to-expiry.
        (params.dateAtCalculation?.getTime() ?? Date.now()) +
        (params.timeToExpirationInYears ?? 0) * YEAR_IN_MS;

    return {
      strike: params.strike,
      isCall: isCallType(params.type),
      volatility: params.volatility,
      riskFreeRate: params.riskFreeRate,
      signedContracts,
      entryPremium,
      expiryMs,
    };
  });

  const originalContractValue = legs.reduce(
    (sum, leg) => sum + leg.entryPremium * leg.signedContracts,
    0,
  );
  const originalStockPositionValue = underlyingStockPositions.reduce(
    (sum, stock) => sum + stock.quantity * stock.price,
    0,
  );

  return {
    legs,
    stocks: underlyingStockPositions,
    originalContractValue,
    originalStockPositionValue,
  };
}

// Coerce any non-finite greek (NaN *or* ±Infinity) to 0 before it reaches the
// chart/table. evalLeg's expiry limit means this should now never fire in
// practice, but it's a cheap last line of defense against a degenerate leg
// (e.g. volatility resolved to 0 → 0/0) leaking Infinity into the UI.
function safe(value: number): number {
  return Number.isFinite(value) ? value : 0;
}

/**
 * Evaluate the whole strategy at a single (spot, time). This is the per-frame
 * unit of work for the slider: a handful of arithmetic ops per leg, no
 * allocation beyond the returned object.
 */
export function evaluateAt(kernel: StrategyKernel, spot: number, tMs: number): StrategyPoint {
  let newContractValue = 0;
  let delta = 0;
  let gamma = 0;
  let theta = 0;
  let vega = 0;
  let rho = 0;

  for (const leg of kernel.legs) {
    // Per-leg time-to-expiry: a calendar/diagonal has each leg on its own clock,
    // so at any scrub time some legs can be live while an earlier-dated leg is
    // already expired. evalLeg settles each expired leg to its intrinsic limit.
    const rawT = (leg.expiryMs - tMs) / YEAR_IN_MS;
    const g = evalLeg(spot, leg.strike, rawT, leg.volatility, leg.riskFreeRate, leg.isCall);

    newContractValue += g.premium * leg.signedContracts;
    const scale = leg.signedContracts * OPTIONS_PER_CONTRACT;
    delta += safe(g.delta) * scale;
    gamma += safe(g.gamma) * scale;
    theta += safe(g.theta) * scale;
    vega += safe(g.vega) * scale;
    rho += safe(g.rho) * scale;
  }

  // Underlying stock P&L + delta (stock contributes delta = 1 per share).
  let stockProfit = 0;
  for (const stock of kernel.stocks) {
    stockProfit += stock.quantity * (spot - stock.price);
    delta += stock.quantity;
  }

  const profitFromOptions = newContractValue - kernel.originalContractValue;
  const profit = profitFromOptions * OPTIONS_PER_CONTRACT + stockProfit;

  return {
    stockPrice: spot,
    profitPerContract: profitFromOptions,
    profit,
    ...calculateReturnOnCost({
      profit,
      netEntryCost:
        kernel.originalContractValue * OPTIONS_PER_CONTRACT + kernel.originalStockPositionValue,
    }),
    contractValue: newContractValue,
    delta,
    gamma,
    theta,
    vega,
    rho,
  };
}

/** Just the P&L at a point — the hot inner used by breakeven refinement. */
export function profitAt(kernel: StrategyKernel, spot: number, tMs: number): number {
  let newContractValue = 0;
  for (const leg of kernel.legs) {
    const rawT = (leg.expiryMs - tMs) / YEAR_IN_MS;
    newContractValue +=
      evalLeg(spot, leg.strike, rawT, leg.volatility, leg.riskFreeRate, leg.isCall).premium *
      leg.signedContracts;
  }
  let stockProfit = 0;
  for (const stock of kernel.stocks) {
    stockProfit += stock.quantity * (spot - stock.price);
  }
  return (newContractValue - kernel.originalContractValue) * OPTIONS_PER_CONTRACT + stockProfit;
}

/** Refine a breakeven between two bracketing spots via bisection on profit. */
function refineBreakeven(
  kernel: StrategyKernel,
  tMs: number,
  lowSpot: number,
  highSpot: number,
  epsilon = 0.001,
): number {
  let lo = lowSpot;
  let hi = highSpot;
  let mid = (lo + hi) / 2;
  for (let i = 0; i < 60 && hi - lo > epsilon; i++) {
    mid = (lo + hi) / 2;
    const p = profitAt(kernel, mid, tMs);
    if (Math.abs(p) <= epsilon) break;
    // Keep the sub-interval where profit changes sign.
    if (Math.sign(p) === Math.sign(profitAt(kernel, lo, tMs))) lo = mid;
    else hi = mid;
  }
  return parseFloat(mid.toFixed(2));
}

export interface CurveResult {
  payoff: StrategyPoint[];
  breakEvens: number[];
}

/**
 * Sweep spot from `startSpot` to `endSpot` at a fixed evaluation time, returning
 * the payoff curve plus refined breakeven prices. This replaces
 * StrategyBuilder.calculateGraphDataPoints.
 */
/**
 * Build the ascending spot grid to sample. Base is the uniform grid
 * `startSpot + i·step`; with `injectStrikes` we additionally sample exactly at
 * each leg strike inside the range. That matters at expiration: the payoff is
 * piecewise-linear with a kink at every strike, and without a sample point
 * sitting *on* the strike the rendered line chords across the corner and rounds
 * it. Injecting the strike makes the hockey-stick corner mathematically exact.
 *
 * The uniform floats are preserved bit-for-bit (strikes are merged in, never
 * replacing a grid point), and the result is a pure function of
 * (startSpot, endSpot, steps, strikes) — so the static payoff graph and the
 * live scrubbed curve, which sweep the same kernel/bounds, stay index-aligned.
 */
export function buildSpotGrid(
  kernel: StrategyKernel,
  startSpot: number,
  endSpot: number,
  steps: number,
  injectStrikes: boolean,
): number[] {
  const priceStep = (endSpot - startSpot) / steps;
  const spots: number[] = new Array(steps + 1);
  for (let i = 0; i <= steps; i++) spots[i] = startSpot + i * priceStep;

  if (!injectStrikes) return spots;

  // A strike is "already covered" if a uniform point lands within a hair of it;
  // in that case we keep the exact uniform float rather than a near-duplicate x.
  const tolerance = Math.abs(priceStep) * 1e-6 + 1e-9;
  const extras: number[] = [];
  for (const leg of kernel.legs) {
    const k = leg.strike;
    if (!(k > startSpot && k < endSpot)) continue;
    if (extras.some((e) => Math.abs(e - k) <= tolerance)) continue;
    const nearestUniform = Math.round((k - startSpot) / priceStep) * priceStep + startSpot;
    if (Math.abs(nearestUniform - k) <= tolerance) continue;
    extras.push(k);
  }
  if (extras.length === 0) return spots;

  return spots.concat(extras).sort((a, b) => a - b);
}

export function sweepCurve(
  kernel: StrategyKernel,
  startSpot: number,
  endSpot: number,
  steps: number,
  tMs: number,
  injectStrikes = false,
): CurveResult {
  const spots = buildSpotGrid(kernel, startSpot, endSpot, steps, injectStrikes);
  const payoff: StrategyPoint[] = new Array(spots.length);
  const breakEvens: number[] = [];

  for (let i = 0; i < spots.length; i++) {
    const spot = spots[i];
    payoff[i] = evaluateAt(kernel, spot, tMs);

    const profit = parseFloat(payoff[i].profit.toFixed(2));
    if (profit === 0) {
      payoff[i].isZeroCrossing = true;
      breakEvens.push(parseFloat(spot.toFixed(2)));
    } else if (
      i > 0 &&
      parseFloat(payoff[i - 1].profit.toFixed(2)) !== 0 &&
      Math.sign(payoff[i].profit) !== Math.sign(payoff[i - 1].profit)
    ) {
      breakEvens.push(
        refineBreakeven(kernel, tMs, Math.max(0, payoff[i - 1].stockPrice - 0.03), spot + 0.03),
      );
    }
  }

  return { payoff, breakEvens: breakEvens.sort((a, b) => a - b) };
}

/**
 * Precompute a spot × time grid for instant scrubbing. Row j is the payoff curve
 * at time `times[j]`. Dragging the slider becomes a row lookup (+ optional lerp)
 * with zero pricing math per frame. Intended to run in a Web Worker after first
 * paint for heavy strategies.
 */
export function sweepGrid(
  kernel: StrategyKernel,
  startSpot: number,
  endSpot: number,
  spotSteps: number,
  times: number[],
): { spots: Float64Array; profits: Float64Array[] } {
  const spots = new Float64Array(spotSteps + 1);
  const priceStep = (endSpot - startSpot) / spotSteps;
  for (let i = 0; i <= spotSteps; i++) spots[i] = startSpot + i * priceStep;

  const profits = times.map((tMs) => {
    const row = new Float64Array(spotSteps + 1);
    for (let i = 0; i <= spotSteps; i++) row[i] = profitAt(kernel, spots[i], tMs);
    return row;
  });

  return { spots, profits };
}
